import React, { useMemo } from "react";
import { useLanguage } from "../../hooks/context-provider-hooks/use-language";
import { useTenant } from "../../hooks/context-provider-hooks/use-tenant";
import { useTextTemplatesByLanguage } from "../../hooks/context-provider-hooks/use-text-templates-by-language";
import { getLocalizedString, makeScopedTextTemplateKey } from "../../utils/string-utils";
import { TextTemplateKey } from "../types";
import { LocalizedTextProps } from "./text.types";

/**
 * The `LocalizedText` text component is a low level component to render localized strings within JSX.
 */
export const LocalizedText = <T extends TextTemplateKey>({
  textTemplateKey,
  textTemplateData,
  withTenant = false,
  defaultText,
}: LocalizedTextProps<T>) => {
  const language = useLanguage(true);
  const textTemplatesByLanguage = useTextTemplatesByLanguage(true);
  const tenant = useTenant(true);

  const localizedString = useMemo(() => {
    const maybeScopes = withTenant ? [tenant] : undefined;
    const maybeScopedTextTemplateKey = makeScopedTextTemplateKey(textTemplateKey, maybeScopes);
    const localizedString = getLocalizedString(
      language,
      textTemplatesByLanguage ?? {},
      maybeScopedTextTemplateKey,
      textTemplateData
    );

    if (localizedString === maybeScopedTextTemplateKey && defaultText) {
      return defaultText;
    } else {
      return localizedString;
    }
  }, [defaultText, language, tenant, textTemplateData, textTemplateKey, textTemplatesByLanguage, withTenant]);

  return <>{localizedString}</>;
};
